import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Camera, CheckCircle2, Loader2, Trash2, Upload, X } from 'lucide-react';
import { usePiercingWorkflow, PiercingAct } from '../contexts/PiercingWorkflowContext';
import PiercingWorkflowStepper from './PiercingWorkflowStepper';
import { apiClient } from '../utils/api';
import { deleteActPhoto, listActPhotos, uploadActPhoto } from '../services/actPhotosApi';
import type { ActPhoto, ActPhotoType } from '../components/camera/types';
import { useToast } from '../components/Toast';
import { ConfirmModal } from '../components/ConfirmModal';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { appConfig } from '../config/app';
import { DateInput } from '../components/DateInput';

function photosOfType(photos: ActPhoto[], type: ActPhotoType): ActPhoto[] {
  return photos
    .filter((p) => p.type === type)
    .sort((a, b) => new Date(a.takenAt).getTime() - new Date(b.takenAt).getTime());
}

/** Caméra (getUserMedia) + import fichier — produit un `File` pour l’upload API. */
function PhotoCameraOrImport(props: {
  compact?: boolean;
  disabled?: boolean;
  busy?: boolean;
  onPhoto: (file: File) => void;
}) {
  const { compact, disabled, busy, onPhoto } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [camError, setCamError] = useState<string | null>(null);

  useEffect(() => {
    if (!cameraOpen) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (video && stream) {
      video.srcObject = stream;
      video.play().catch(() => {});
    }
  }, [cameraOpen]);

  useEffect(() => () => { streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
    setCamError(null);
  };

  const openCamera = async () => {
    setCamError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      setCameraOpen(true);
    } catch {
      setCamError('Caméra indisponible — utilisez « Importer ».');
    }
  };

  const captureFromCamera = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onPhoto(file);
        closeCamera();
      },
      'image/jpeg',
      0.92,
    );
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    onPhoto(file);
  };

  const btnClass = compact
    ? 'flex-1 flex items-center justify-center gap-2 py-2.5 px-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors text-xs font-medium'
    : 'flex-1 flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors text-sm';

  if (cameraOpen) {
    return (
      <div className="space-y-2">
        <div className={`rounded-xl overflow-hidden bg-black ${compact ? 'aspect-[4/3]' : 'aspect-video'}`}>
          <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay />
        </div>
        <canvas ref={canvasRef} className="hidden" />
        <div className="flex gap-2">
          <button type="button" onClick={captureFromCamera} disabled={disabled || busy}
            className="btn-primary flex items-center gap-2 flex-1 justify-center text-sm py-2.5">
            <Camera className="w-4 h-4" /> Capturer
          </button>
          <button type="button" onClick={closeCamera} disabled={disabled || busy}
            className="px-4 py-2.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm flex items-center gap-1 justify-center">
            <X className="w-4 h-4" /> Annuler
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {camError && <p className="text-xs text-amber-600">{camError}</p>}
      {busy && (
        <p className="text-xs text-primary-600 flex items-center gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement sur le serveur…
        </p>
      )}
      <div className="flex gap-2">
        <button type="button" onClick={openCamera} disabled={disabled || busy} className={btnClass}>
          <Camera className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} /> Caméra
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={disabled || busy} className={btnClass}>
          <Upload className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} /> Importer
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
      </div>
    </div>
  );
}

interface CapturedPhoto {
  id: string;
  url: string;
}

interface ActCapture {
  resultPhotos: CapturedPhoto[];
  labelPhotos: CapturedPhoto[];
  batchNumber: string;
  sterilizationDate: string;
  expiryDate: string;
  jewelryRef: string;
  needleGauge: string;
  done: boolean;
}

const emptyCaptureBase = (): ActCapture => ({
  resultPhotos: [],
  labelPhotos: [],
  batchNumber: '',
  sterilizationDate: '',
  expiryDate: '',
  jewelryRef: '',
  needleGauge: '',
  done: false,
});

function hasResultEvidence(cap: ActCapture | null | undefined): boolean {
  return (cap?.resultPhotos?.length ?? 0) > 0;
}

function hasLabelEvidence(cap: ActCapture | null | undefined): boolean {
  return (cap?.labelPhotos?.length ?? 0) > 0;
}

/** Manual lot path: n° de lot + (réf. produit ou calibre). */
function hasManualTraceabilityEvidence(cap: ActCapture | null | undefined): boolean {
  if (!cap) return false;
  const batch = (cap.batchNumber ?? '').trim();
  const ref = (cap.jewelryRef ?? '').trim();
  const gauge = (cap.needleGauge ?? '').trim();
  const sterilization = (cap.sterilizationDate ?? '').trim();
  const expiry = (cap.expiryDate ?? '').trim();
  return (
    batch.length > 0 ||
    ref.length > 0 ||
    gauge.length > 0 ||
    sterilization.length > 0 ||
    expiry.length > 0
  );
}

function hasTraceabilityEvidence(cap: ActCapture | null | undefined): boolean {
  return hasLabelEvidence(cap) || hasManualTraceabilityEvidence(cap);
}

function getCaptureRequirementMessage(cap: ActCapture | null | undefined): string | null {
  if (!hasTraceabilityEvidence(cap) && !hasResultEvidence(cap)) {
    return 'Ajoutez une photo ou renseignez la traçabilité.';
  }
  return null;
}

function PhotoThumbGrid(props: {
  photos: CapturedPhoto[];
  onDelete: (photoId: string) => void;
  deletingId: string | null;
  compact?: boolean;
}) {
  const { photos, onDelete, deletingId, compact } = props;
  if (!photos.length) {
    return (
      <div
        className={`flex items-center justify-center rounded-xl bg-gray-900 text-gray-500 ${compact ? 'h-36' : ''}`}
        style={compact ? undefined : { minHeight: '220px' }}
      >
        <div className="text-center p-4">
          <Camera className="w-10 h-10 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Aucune photo</p>
        </div>
      </div>
    );
  }
  return (
    <div className={`grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
      {photos.map((photo, idx) => (
        <div key={photo.id} className="relative group rounded-xl overflow-hidden bg-gray-900 border border-gray-700">
          <img
            src={photo.url}
            alt={`Photo ${idx + 1}`}
            className={`w-full object-contain ${compact ? 'h-28' : 'h-36'}`}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[10px] px-2 py-1">
            {idx + 1}/{photos.length}
          </div>
          <button
            type="button"
            title="Supprimer"
            disabled={deletingId === photo.id}
            onClick={() => onDelete(photo.id)}
            className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-red-600/90 text-white hover:bg-red-700 disabled:opacity-50 opacity-90 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
          >
            {deletingId === photo.id ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      ))}
    </div>
  );
}

const PiercingCapturePage: React.FC = () => {
  const navigate = useNavigate();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { acts, loading, appointment, updateActLocal, removeActLocal } = usePiercingWorkflow();
  const { showToast, ToastComponent } = useToast();
  const maxResultPhotos = appConfig.maxResultPhotos;
  const maxLabelPhotos = appConfig.maxLabelPhotos;

  const [selectedActIndex, setSelectedActIndex] = useState(0);
  const [captures, setCaptures] = useState<Record<string, ActCapture>>({});
  const capturesRef = useRef(captures);
  capturesRef.current = captures;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [traceMode, setTraceMode] = useState<'photo' | 'manual'>('photo');
  const [mobileStep, setMobileStep] = useState<'result' | 'trace'>('result');
  const [photoBusy, setPhotoBusy] = useState<{ actId: string; slot: 'result' | 'label' } | null>(null);
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);
  const [markingActDone, setMarkingActDone] = useState(false);
  const [unconfirming, setUnconfirming] = useState(false);
  const [pendingUnconfirm, setPendingUnconfirm] = useState(false);
  const [deletingActId, setDeletingActId] = useState<string | null>(null);
  const [actPendingDelete, setActPendingDelete] = useState<PiercingAct | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{
    actId: string;
    slot: 'result' | 'label';
    photoId: string;
  } | null>(null);
  const initialHydrateDone = useRef(false);

  const selectedAct: PiercingAct | undefined = acts[selectedActIndex];
  const isActDone = (act: PiercingAct) =>
    !!(captures[act.id]?.done || act.demandDetails?.photosCaptureConfirmed);

  const selectedActCapture = selectedAct ? (captures[selectedAct.id] ?? emptyCaptureBase()) : null;
  const selectedRequirementMessage = getCaptureRequirementMessage(selectedActCapture);
  const canMarkSelectedActDone =
    !!selectedAct && !isActDone(selectedAct) && !selectedRequirementMessage;

  const updateCapture = (actId: string, updates: Partial<ActCapture>) => {
    setCaptures((prev) => ({
      ...prev,
      [actId]: { ...(prev[actId] ?? emptyCaptureBase()), ...updates },
    }));
  };

  const persistPhotoImmediately = async (
    actId: string,
    slot: 'result' | 'label',
    file: File,
    photoType: ActPhotoType,
  ) => {
    const cur = capturesRef.current[actId] ?? emptyCaptureBase();
    const list = slot === 'result' ? cur.resultPhotos : cur.labelPhotos;
    const max = slot === 'result' ? maxResultPhotos : maxLabelPhotos;
    if (list.length >= max) {
      showToast(
        slot === 'result'
          ? `Maximum ${max} photos de résultat atteint.`
          : `Maximum ${max} photos de traçabilité atteint.`,
        'error',
        4000,
      );
      return;
    }

    setPhotoBusy({ actId, slot });
    try {
      const saved = await uploadActPhoto(actId, file, photoType);
      const previewUrl = URL.createObjectURL(file);
      setCaptures((prev) => {
        const c = prev[actId] ?? emptyCaptureBase();
        const key = slot === 'result' ? 'resultPhotos' : 'labelPhotos';
        const existing = c[key];
        if (existing.some((p) => p.id === saved.id) || existing.length >= max) {
          URL.revokeObjectURL(previewUrl);
          return prev;
        }
        return {
          ...prev,
          [actId]: {
            ...c,
            [key]: [...existing, { id: saved.id, url: previewUrl }],
          },
        };
      });
      showToast(
        slot === 'result' ? 'Photo résultat enregistrée' : 'Photo traçabilité enregistrée',
        'success',
        2200,
      );
    } catch (err: any) {
      showToast(err?.message || 'Échec de l’enregistrement de la photo', 'error', 5000);
    } finally {
      setPhotoBusy(null);
    }
  };

  const removePhoto = async (actId: string, slot: 'result' | 'label', photoId: string) => {
    setDeletingPhotoId(photoId);
    try {
      await deleteActPhoto(actId, photoId);

      const act = acts.find((a) => a.id === actId);
      const before = capturesRef.current[actId] ?? emptyCaptureBase();
      const key = slot === 'result' ? 'resultPhotos' : 'labelPhotos';
      const removed = before[key].find((p) => p.id === photoId);
      const after: ActCapture = {
        ...before,
        [key]: before[key].filter((p) => p.id !== photoId),
      };
      if (removed?.url.startsWith('blob:')) URL.revokeObjectURL(removed.url);

      const wasConfirmed = !!(before.done || act?.demandDetails?.photosCaptureConfirmed);
      const requirementAfter = getCaptureRequirementMessage(after);
      const mustInvalidate = wasConfirmed && !!requirementAfter;

      if (mustInvalidate) {
        const res = await apiClient(`/api/practitioner/acts/${actId}/demand-details`, {
          method: 'PUT',
          body: JSON.stringify({ photosCaptureConfirmed: false }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(
            (err as { message?: string }).message ||
              'Impossible d’annuler la validation de l’acte après suppression',
          );
        }
        const dd =
          act?.demandDetails && typeof act.demandDetails === 'object'
            ? { ...act.demandDetails }
            : {};
        updateActLocal(actId, {
          demandDetails: { ...dd, photosCaptureConfirmed: false },
        });
        after.done = false;
      }

      setCaptures((prev) => ({
        ...prev,
        [actId]: after,
      }));

      if (mustInvalidate) {
        showToast(
          'Photo supprimée — validation de l’acte annulée (preuves insuffisantes). Revalidez l’acte après correction.',
          'warning',
          5000,
        );
      } else {
        showToast('Photo supprimée', 'success', 1800);
      }
    } catch (err: any) {
      showToast(err?.message || 'Impossible de supprimer la photo', 'error', 4000);
    } finally {
      setDeletingPhotoId(null);
      setPendingDelete(null);
    }
  };

  const requestRemovePhoto = (actId: string, slot: 'result' | 'label', photoId: string) => {
    setPendingDelete({ actId, slot, photoId });
  };

  useEffect(() => {
    if (loading || !acts.length) return;
    if (initialHydrateDone.current) return;
    initialHydrateDone.current = true;

    let cancelled = false;
    const createdUrls: string[] = [];

    (async () => {
      for (const act of acts) {
        if (cancelled) return;
        try {
          const photos = await listActPhotos(act.id);
          const resultMetas = photosOfType(photos, 'RESULT').slice(0, maxResultPhotos);
          const labelMetas = photosOfType(photos, 'LABEL').slice(0, maxLabelPhotos);

          const loadOne = async (meta: ActPhoto): Promise<CapturedPhoto | null> => {
            try {
              const r = await apiClient(meta.url);
              if (!r.ok) return null;
              const url = URL.createObjectURL(await r.blob());
              createdUrls.push(url);
              return { id: meta.id, url };
            } catch {
              return null;
            }
          };

          const resultPhotos = (await Promise.all(resultMetas.map(loadOne))).filter(Boolean) as CapturedPhoto[];
          const labelPhotos = (await Promise.all(labelMetas.map(loadOne))).filter(Boolean) as CapturedPhoto[];

          if (cancelled) return;

          setCaptures((prev) => {
            const cur = prev[act.id] ?? emptyCaptureBase();
            if (cur.resultPhotos.length > 0 || cur.labelPhotos.length > 0) {
              return prev;
            }
            if (!resultPhotos.length && !labelPhotos.length) return prev;
            return {
              ...prev,
              [act.id]: { ...cur, resultPhotos, labelPhotos },
            };
          });
        } catch {
          /* ignore */
        }
      }
    })();

    return () => {
      cancelled = true;
      createdUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [loading, acts, maxResultPhotos, maxLabelPhotos]);

  useEffect(() => {
    if (loading || !acts.length) return;
    setCaptures((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const act of acts) {
        const confirmed = !!act.demandDetails?.photosCaptureConfirmed;
        const cur = next[act.id] ?? emptyCaptureBase();
        if (cur.done !== confirmed) {
          next[act.id] = { ...cur, done: confirmed };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [loading, acts]);

  useEffect(() => () => {
    Object.values(capturesRef.current).forEach((c) => {
      c.resultPhotos.forEach((p) => {
        if (p.url.startsWith('blob:')) URL.revokeObjectURL(p.url);
      });
      c.labelPhotos.forEach((p) => {
        if (p.url.startsWith('blob:')) URL.revokeObjectURL(p.url);
      });
    });
  }, []);

  useEffect(() => {
    if (loading || !acts.length) return;
    setCaptures((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const act of acts) {
        const jewelry =
          act.piercingRecord?.jewelry && typeof act.piercingRecord.jewelry === 'object'
            ? (act.piercingRecord.jewelry as Record<string, unknown>)
            : {};
        const cur = next[act.id] ?? emptyCaptureBase();
        const patch: Partial<ActCapture> = {};
        if (!cur.batchNumber && jewelry.batchNumber) {
          patch.batchNumber = String(jewelry.batchNumber);
        }
        if (!cur.jewelryRef && jewelry.reference) {
          patch.jewelryRef = String(jewelry.reference);
        }
        if (!cur.needleGauge && (act.piercingRecord?.needleGauge || jewelry.needleGauge)) {
          patch.needleGauge = String(act.piercingRecord?.needleGauge || jewelry.needleGauge);
        }
        if (!cur.sterilizationDate && jewelry.sterilizationDate) {
          patch.sterilizationDate = String(jewelry.sterilizationDate);
        }
        if (!cur.expiryDate && jewelry.expiryDate) {
          patch.expiryDate = String(jewelry.expiryDate);
        }
        if (Object.keys(patch).length > 0) {
          next[act.id] = { ...cur, ...patch };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [loading, acts]);

  const markActDone = async () => {
    if (!selectedAct) return;
    const cap = captures[selectedAct.id] ?? emptyCaptureBase();
    const requirement = getCaptureRequirementMessage(cap);
    if (requirement) {
      showToast(requirement, 'error', 5000);
      setError(requirement);
      return;
    }

    setMarkingActDone(true);
    setError(null);
    try {
      if (hasManualTraceabilityEvidence(cap) || cap.sterilizationDate || cap.expiryDate) {
        const matRes = await apiClient(`/api/practitioner/acts/${selectedAct.id}/piercing-material`, {
          method: 'POST',
          body: JSON.stringify({
            jewelry: {
              reference: cap.jewelryRef || undefined,
              material: (selectedAct.demandDetails?.jewelry as any)?.material || undefined,
            },
            needleGauge: cap.needleGauge || undefined,
            batchNumber: cap.batchNumber || undefined,
            sterilizationDate: cap.sterilizationDate || undefined,
            expiryDate: cap.expiryDate || undefined,
            labelPhotoUrl: hasLabelEvidence(cap) ? 'uploaded' : undefined,
          }),
        });
        if (!matRes.ok) {
          const err = await matRes.json().catch(() => ({}));
          throw new Error((err as { message?: string }).message || 'Erreur enregistrement traçabilité');
        }
      }

      const res = await apiClient(`/api/practitioner/acts/${selectedAct.id}/demand-details`, {
        method: 'PUT',
        body: JSON.stringify({ photosCaptureConfirmed: true }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Erreur serveur');
      }
      const dd =
        selectedAct.demandDetails && typeof selectedAct.demandDetails === 'object'
          ? { ...selectedAct.demandDetails }
          : {};
      updateActLocal(selectedAct.id, {
        demandDetails: { ...dd, photosCaptureConfirmed: true },
      });
      updateCapture(selectedAct.id, { done: true });
      if (selectedActIndex < acts.length - 1) {
        setSelectedActIndex(selectedActIndex + 1);
      }
      showToast('Acte enregistré comme terminé', 'success', 2200);
    } catch (err: any) {
      showToast(err?.message || 'Impossible d’enregistrer la confirmation', 'error', 4500);
      setError(err?.message || 'Impossible d’enregistrer la confirmation');
    } finally {
      setMarkingActDone(false);
    }
  };

  const unconfirmAct = async () => {
    if (!selectedAct) return;
    setUnconfirming(true);
    setError(null);
    try {
      const res = await apiClient(`/api/practitioner/acts/${selectedAct.id}/demand-details`, {
        method: 'PUT',
        body: JSON.stringify({ photosCaptureConfirmed: false }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Erreur serveur');
      }
      const dd =
        selectedAct.demandDetails && typeof selectedAct.demandDetails === 'object'
          ? { ...selectedAct.demandDetails }
          : {};
      updateActLocal(selectedAct.id, {
        demandDetails: { ...dd, photosCaptureConfirmed: false },
      });
      updateCapture(selectedAct.id, { done: false });
      setPendingUnconfirm(false);
      showToast('Validation annulée — vous pouvez modifier les photos et la traçabilité', 'success', 3200);
    } catch (err: any) {
      showToast(err?.message || 'Impossible d’annuler la validation', 'error', 4500);
    } finally {
      setUnconfirming(false);
    }
  };

  const executeDeleteAct = async (act: PiercingAct) => {
    setDeletingActId(act.id);
    try {
      const res = await apiClient(`/api/practitioner/acts/${act.id}`, { method: 'DELETE' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast((body as { message?: string }).message || "Impossible de supprimer l'acte", 'error', 4500);
        return;
      }
      const cap = capturesRef.current[act.id];
      if (cap) {
        cap.resultPhotos.forEach((p) => {
          if (p.url.startsWith('blob:')) URL.revokeObjectURL(p.url);
        });
        cap.labelPhotos.forEach((p) => {
          if (p.url.startsWith('blob:')) URL.revokeObjectURL(p.url);
        });
      }
      setCaptures((prev) => {
        const next = { ...prev };
        delete next[act.id];
        return next;
      });
      removeActLocal(act.id);
      setActPendingDelete(null);
      setSelectedActIndex((idx) => {
        const remainingCount = Math.max(0, acts.length - 1);
        if (remainingCount === 0) return 0;
        return Math.min(idx, remainingCount - 1);
      });
      showToast(`Acte #${act.sequence} et images associées supprimés`, 'success', 2800);
    } catch {
      showToast("Erreur lors de la suppression de l'acte", 'error', 4500);
    } finally {
      setDeletingActId(null);
    }
  };

  const allDone = acts.every((a) => isActDone(a));

  useEffect(() => {
    setMobileStep('result');
    setError(null);
  }, [selectedActIndex]);

  const throwIfBadResponse = async (res: Response, fallback: string) => {
    if (res.ok) return;
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { message?: string }).message || fallback);
  };

  const handleContinue = async () => {
    if (!appointmentId) return;

    for (const act of acts) {
      const cap = captures[act.id] ?? emptyCaptureBase();
      const requirement = getCaptureRequirementMessage(cap);
      if (requirement) {
        setError(`Acte ${act.sequence} : ${requirement}`);
        showToast(`Acte ${act.sequence} : ${requirement}`, 'error', 4500);
        return;
      }
    }

    setSaving(true);
    setError(null);

    try {
      for (const act of acts) {
        const cap = captures[act.id] ?? emptyCaptureBase();

        const matRes = await apiClient(`/api/practitioner/acts/${act.id}/piercing-material`, {
          method: 'POST',
          body: JSON.stringify({
            jewelry: {
              reference: cap.jewelryRef || undefined,
              material: (act.demandDetails?.jewelry as any)?.material || undefined,
            },
            needleGauge: cap.needleGauge || undefined,
            batchNumber: cap.batchNumber || undefined,
            sterilizationDate: cap.sterilizationDate || undefined,
            expiryDate: cap.expiryDate || undefined,
            labelPhotoUrl: hasLabelEvidence(cap) ? 'uploaded' : undefined,
          }),
        });
        await throwIfBadResponse(matRes, 'Erreur enregistrement traçabilité');

        const res = await apiClient(`/api/practitioner/acts/${act.id}/demand-details`, {
          method: 'PUT',
          body: JSON.stringify({ photosCaptureConfirmed: true }),
        });
        await throwIfBadResponse(res, 'Erreur serveur confirmation acte');

        const dd =
          act.demandDetails && typeof act.demandDetails === 'object'
            ? { ...act.demandDetails }
            : {};
        updateActLocal(act.id, {
          demandDetails: { ...dd, photosCaptureConfirmed: true },
        });
        updateCapture(act.id, { done: true });
      }

      navigate(`/practitioner/piercing/${appointmentId}/finalization`);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'enregistrement.');
      showToast(err.message || 'Erreur lors de l\'enregistrement.', 'error', 4500);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !appointment) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  const resultCount = selectedActCapture?.resultPhotos.length ?? 0;
  const labelCount = selectedActCapture?.labelPhotos.length ?? 0;
  const canAddResult = resultCount < maxResultPhotos;
  const canAddLabel = labelCount < maxLabelPhotos;
  const resultComplete = resultCount > 0;
  const traceComplete = hasTraceabilityEvidence(selectedActCapture);
  const mobileStepIndex = mobileStep === 'result' ? 0 : 1;

  const goToMobileTrace = () => {
    setError(null);
    setMobileStep('trace');
  };

  const handleMobileConfirmAndPass = async () => {
    await handleContinue();
  };

  return (
    <>
      <ToastComponent />
      <PiercingWorkflowStepper currentStep={2} />

      {/* Mobile: workflow séquentiel en 2 écrans (Résultat -> Traçabilité & Validation) */}
      <div className="lg:hidden">
        {selectedAct && selectedActCapture && (
          <div className="min-h-[calc(100dvh-9rem)] pb-28">
            <div className="px-4 pt-3 pb-4">
              <div className="flex items-center justify-between gap-3 mb-4">
                <button
                  type="button"
                  className="text-sm font-medium text-gray-600 inline-flex items-center gap-1"
                  onClick={() => {
                    if (mobileStep === 'trace') setMobileStep('result');
                    else navigate(`/practitioner/piercing/${appointmentId}/contract`);
                    setError(null);
                  }}
                >
                  <span aria-hidden>←</span> Retour
                </button>
                <p className="text-sm font-semibold text-gray-900">Acte {selectedAct.sequence}</p>
                <span className="text-xs text-gray-500 tabular-nums">{mobileStepIndex + 1}/2</span>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-6" aria-label="Progression">
                {['Résultat', 'Traçabilité'].map((label, idx) => (
                  <div key={label} className="space-y-1.5">
                    <div className={`h-1.5 rounded-full ${idx <= mobileStepIndex ? 'bg-primary-500' : 'bg-gray-200'}`} />
                    <p className={`text-[10px] text-center ${idx === mobileStepIndex ? 'font-semibold text-primary-700' : 'text-gray-400'}`}>
                      {label}
                    </p>
                  </div>
                ))}
              </div>

              {mobileStep === 'result' && (
                <section>
                  <div className="text-center mb-5">
                    <h1 className="text-xl font-bold text-gray-900">Photo du résultat</h1>
                    <p className="text-sm text-gray-500 mt-1">Prenez une photo claire du piercing terminé.</p>
                  </div>
                  <div className="rounded-2xl overflow-hidden bg-gray-950 border border-gray-800 min-h-[320px] flex items-center justify-center">
                    {selectedActCapture.resultPhotos.length > 0 ? (
                      <img
                        src={selectedActCapture.resultPhotos[selectedActCapture.resultPhotos.length - 1].url}
                        alt="Dernière photo du résultat"
                        className="w-full max-h-[58dvh] object-contain"
                      />
                    ) : (
                      <div className="text-center px-6 py-14 text-gray-400">
                        <Camera className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                        <p className="font-medium text-gray-300">Aucune photo du résultat</p>
                        <p className="text-xs mt-1">La photo est obligatoire pour continuer.</p>
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    {canAddResult ? (
                      <PhotoCameraOrImport
                        disabled={isActDone(selectedAct)}
                        busy={photoBusy?.actId === selectedAct.id && photoBusy.slot === 'result'}
                        onPhoto={(file) => void persistPhotoImmediately(selectedAct.id, 'result', file, 'RESULT')}
                      />
                    ) : (
                      <p className="text-xs text-center text-gray-500">Maximum {maxResultPhotos} photos atteint.</p>
                    )}
                  </div>
                  {selectedActCapture.resultPhotos.length > 1 && (
                    <div className="mt-5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Photos ajoutées</p>
                      <PhotoThumbGrid compact photos={selectedActCapture.resultPhotos} deletingId={deletingPhotoId} onDelete={(id) => requestRemovePhoto(selectedAct.id, 'result', id)} />
                    </div>
                  )}
                </section>
              )}

              {mobileStep === 'trace' && (
                <section>
                  <div className="text-center mb-5">
                    <h1 className="text-xl font-bold text-gray-900">Traçabilité</h1>
                    <p className="text-sm text-gray-500 mt-1">Photographiez les étiquettes ou saisissez le lot manuellement.</p>
                  </div>
                  <div className="p-1 bg-gray-100 rounded-xl grid grid-cols-2 mb-5">
                    <button type="button" className={`py-2.5 rounded-lg text-sm font-semibold transition ${traceMode === 'photo' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`} onClick={() => setTraceMode('photo')}>Photo</button>
                    <button type="button" className={`py-2.5 rounded-lg text-sm font-semibold transition ${traceMode === 'manual' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`} onClick={() => setTraceMode('manual')}>Manuel</button>
                  </div>
                  {traceMode === 'photo' ? (
                    <div>
                      <div className="rounded-2xl overflow-hidden bg-gray-950 border border-gray-800 min-h-[260px] flex items-center justify-center">
                        {selectedActCapture.labelPhotos.length > 0 ? (
                          <img src={selectedActCapture.labelPhotos[selectedActCapture.labelPhotos.length - 1].url} alt="Dernière photo de traçabilité" className="w-full max-h-[45dvh] object-contain" />
                        ) : (
                          <div className="text-center px-6 py-12 text-gray-400">
                            <Camera className="w-11 h-11 mx-auto mb-3 text-gray-600" />
                            <p className="font-medium text-gray-300">Photo des étiquettes</p>
                            <p className="text-xs mt-1">Bijou, aiguille ou emballage stérile.</p>
                          </div>
                        )}
                      </div>
                      <div className="mt-4">
                        {canAddLabel ? (
                          <PhotoCameraOrImport compact disabled={isActDone(selectedAct)} busy={photoBusy?.actId === selectedAct.id && photoBusy.slot === 'label'} onPhoto={(file) => void persistPhotoImmediately(selectedAct.id, 'label', file, 'LABEL')} />
                        ) : (
                          <p className="text-xs text-center text-gray-500">Maximum {maxLabelPhotos} photos atteint.</p>
                        )}
                      </div>
                    </div>
                  ) : (
                   <div className="space-y-4">
  <div>
    <label
      htmlFor={`mobile-trace-ref-${selectedAct.id}`}
      className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1.5"
    >
      Réf. bijou / aiguille <span className="text-red-500">*</span>
    </label>
    <input
      id={`mobile-trace-ref-${selectedAct.id}`}
      type="text"
      className="input w-full text-base"
      placeholder="Ex: TISGRBN1.2x08x3/3-HP"
      value={selectedActCapture.jewelryRef}
      onChange={(e) =>
        updateCapture(selectedAct.id, { jewelryRef: e.target.value })
      }
    />
  </div>

  <div>
    <label
      htmlFor={`mobile-trace-gauge-${selectedAct.id}`}
      className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1.5"
    >
      Calibre / Gauge{' '}
      <span className="text-gray-400 font-normal normal-case tracking-normal">
        (ou réf.)
      </span>
    </label>
    <input
      id={`mobile-trace-gauge-${selectedAct.id}`}
      type="text"
      className="input w-full text-base"
      placeholder="Ex: 16GA"
      value={selectedActCapture.needleGauge}
      onChange={(e) =>
        updateCapture(selectedAct.id, { needleGauge: e.target.value })
      }
    />
  </div>

  <div>
    <label
      htmlFor={`mobile-trace-batch-${selectedAct.id}`}
      className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1.5"
    >
      N° Stérilisation / Lot <span className="text-red-500">*</span>
    </label>
    <input
      id={`mobile-trace-batch-${selectedAct.id}`}
      type="text"
      className="input w-full text-base"
      placeholder="Ex: 250915"
      value={selectedActCapture.batchNumber}
      onChange={(e) =>
        updateCapture(selectedAct.id, { batchNumber: e.target.value })
      }
    />
  </div>

  <div className="grid grid-cols-2 gap-3">
    <div>
      <label className="text-xs text-gray-500 font-semibold block mb-1.5">
        Stérilisation
      </label>
      <DateInput
        value={selectedActCapture.sterilizationDate}
        onChange={(value) =>
          updateCapture(selectedAct.id, { sterilizationDate: value })
        }
        className="input w-full text-sm"
      />
    </div>

    <div>
      <label className="text-xs text-gray-500 font-semibold block mb-1.5">
        Utiliser avant
      </label>
      <DateInput
        value={selectedActCapture.expiryDate}
        onChange={(value) =>
          updateCapture(selectedAct.id, { expiryDate: value })
        }
        className="input w-full text-sm"
      />
    </div>
  </div>
</div>
                  )}
                </section>
              )}

              {error && <div className="mt-5 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}
            </div>

            <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-gray-200 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <div className="max-w-md mx-auto">
                {mobileStep === 'result' && <button type="button" className="btn-primary w-full h-14 text-sm font-bold disabled:opacity-40" disabled={!!photoBusy || !!deletingPhotoId} onClick={goToMobileTrace}>Traçabilité →</button>}
                {mobileStep === 'trace' && <button type="button" className="btn-primary w-full h-14 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40" disabled={saving || markingActDone || !!photoBusy || !!deletingPhotoId} onClick={() => void handleMobileConfirmAndPass()}>{saving || markingActDone ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Confirmer et passer en caisse →</button>}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="hidden lg:grid grid-cols-12 gap-6 min-h-[600px]">
        <div className="col-span-12 lg:col-span-3 flex flex-col gap-3">
          {acts.map((act, idx) => {
            const cap = captures[act.id];
            const isDone = isActDone(act);
            const isSelected = idx === selectedActIndex;
            const thumb = cap?.resultPhotos[0]?.url;
            return (
              <div
                key={act.id}
                className={`rounded-xl border-2 transition-all overflow-hidden
                  ${isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white'}
                  ${isDone ? 'opacity-90' : ''}`}
              >
                <button
                  type="button"
                  className="w-full text-left p-3 hover:bg-black/[0.02]"
                  onClick={() => setSelectedActIndex(idx)}
                >
                  <div className="flex items-center gap-2">
                    {isDone ? (
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      </div>
                    ) : thumb ? (
                      <img src={thumb} className="w-8 h-8 rounded-full object-cover flex-shrink-0" alt="" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 15.2A3.2 3.2 0 0 1 8.8 12 3.2 3.2 0 0 1 12 8.8a3.2 3.2 0 0 1 3.2 3.2 3.2 3.2 0 0 1-3.2 3.2zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9z" />
                        </svg>
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">Acte {act.sequence}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {act.demandDetails?.bodyZoneId ? 'Zone définie' : 'Zone —'}
                      </p>
                    </div>
                  </div>
                </button>
                {act.status !== 'COMPLETED' && (
                  <div className="px-3 pb-2">
                    <button
                      type="button"
                      className="w-full text-xs text-red-600 border border-red-200 rounded-lg py-1.5 hover:bg-red-50 flex items-center justify-center gap-1 disabled:opacity-50"
                      disabled={deletingActId === act.id}
                      onClick={() => setActPendingDelete(act)}
                      title="Supprimer cet acte et toutes ses images"
                    >
                      {deletingActId === act.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                      Supprimer l&apos;acte
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="col-span-12 lg:col-span-5">
          {selectedAct && selectedActCapture && (
            <div className="card h-full flex flex-col">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <h3 className="font-bold">
                    Photos du résultat — Acte {selectedAct.sequence} <span className="text-red-500">*</span>
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Au moins 1 photo obligatoire · max {maxResultPhotos}
                  </p>
                </div>
                <span className="text-xs font-medium text-gray-500 tabular-nums shrink-0 mt-1">
                  {resultCount}/{maxResultPhotos}
                </span>
              </div>

              <div className="flex-1">
                <PhotoThumbGrid
                  photos={selectedActCapture.resultPhotos}
                  deletingId={deletingPhotoId}
                  onDelete={(id) => requestRemovePhoto(selectedAct.id, 'result', id)}
                />
              </div>

              {canAddResult ? (
                <div className="mt-4">
                  <PhotoCameraOrImport
                    disabled={!selectedAct || isActDone(selectedAct)}
                    busy={photoBusy?.actId === selectedAct.id && photoBusy.slot === 'result'}
                    onPhoto={(file) =>
                      selectedAct && void persistPhotoImmediately(selectedAct.id, 'result', file, 'RESULT')
                    }
                  />
                </div>
              ) : (
                <p className="mt-3 text-xs text-gray-500">
                  Limite atteinte ({maxResultPhotos}). Supprimez une photo pour en ajouter une autre.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
          {selectedAct && selectedActCapture && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold">Traçabilité <span className="text-red-500">*</span></h3>
                  <p className="text-xs text-gray-500 mt-0.5">Photo des étiquettes ou saisie manuelle du lot</p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className={`text-xs px-3 py-1 rounded-full font-medium transition-colors
                      ${traceMode === 'photo' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                    onClick={() => setTraceMode('photo')}
                  >
                    Photo
                  </button>
                  <button
                    type="button"
                    className={`text-xs px-3 py-1 rounded-full font-medium transition-colors
                      ${traceMode === 'manual' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                    onClick={() => setTraceMode('manual')}
                  >
                    Manuel
                  </button>
                </div>
              </div>

              {traceMode === 'photo' ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-500">
                      Étiquettes bijou / aiguille · max {maxLabelPhotos}
                    </p>
                    <span className="text-xs font-medium text-gray-500 tabular-nums">
                      {labelCount}/{maxLabelPhotos}
                    </span>
                  </div>
                  <PhotoThumbGrid
                    compact
                    photos={selectedActCapture.labelPhotos}
                    deletingId={deletingPhotoId}
                    onDelete={(id) => requestRemovePhoto(selectedAct.id, 'label', id)}
                  />
                  {canAddLabel ? (
                    <div className="mt-3">
                      <PhotoCameraOrImport
                        compact
                        disabled={!selectedAct || isActDone(selectedAct)}
                        busy={photoBusy?.actId === selectedAct.id && photoBusy.slot === 'label'}
                        onPhoto={(file) =>
                          selectedAct && void persistPhotoImmediately(selectedAct.id, 'label', file, 'LABEL')
                        }
                      />
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-gray-500">
                      Limite atteinte ({maxLabelPhotos}). Supprimez une photo pour en ajouter une autre.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor={`trace-jewelry-ref-${selectedAct.id}`}
                      className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1.5"
                    >
                      Réf. bijou / aiguille <span className="text-red-500">*</span>
                    </label>
                    <input
                      id={`trace-jewelry-ref-${selectedAct.id}`}
                      type="text"
                      className="input w-full text-sm"
                      placeholder="Ex: TISGRBN1.2x08x3/3-HP"
                      value={selectedActCapture.jewelryRef}
                      onChange={(e) => updateCapture(selectedAct.id, { jewelryRef: e.target.value })}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={`trace-needle-gauge-${selectedAct.id}`}
                      className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1.5"
                    >
                      Calibre / Gauge <span className="text-gray-400 font-normal normal-case tracking-normal">(ou réf. ci-dessus)</span>
                    </label>
                    <input
                      id={`trace-needle-gauge-${selectedAct.id}`}
                      type="text"
                      className="input w-full text-sm"
                      placeholder="Ex: 16GA"
                      value={selectedActCapture.needleGauge}
                      onChange={(e) => updateCapture(selectedAct.id, { needleGauge: e.target.value })}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={`trace-batch-${selectedAct.id}`}
                      className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1.5"
                    >
                      N° Stérilisation / Lot <span className="text-red-500">*</span>
                    </label>
                    <input
                      id={`trace-batch-${selectedAct.id}`}
                      type="text"
                      className="input w-full text-sm"
                      placeholder="Ex: 250915"
                      value={selectedActCapture.batchNumber}
                      onChange={(e) => updateCapture(selectedAct.id, { batchNumber: e.target.value })}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={`trace-sterilization-${selectedAct.id}`}
                      className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1.5"
                    >
                      Date de stérilisation
                    </label>
                    <DateInput
                      id={`trace-sterilization-${selectedAct.id}`}
                      className="input w-full text-sm"
                      value={selectedActCapture.sterilizationDate}
                      onChange={(value) => updateCapture(selectedAct.id, { sterilizationDate: value })}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={`trace-expiry-${selectedAct.id}`}
                      className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1.5"
                    >
                      Utiliser avant
                    </label>
                    <DateInput
                      id={`trace-expiry-${selectedAct.id}`}
                      className="input w-full text-sm"
                      value={selectedActCapture.expiryDate}
                      onChange={(value) => updateCapture(selectedAct.id, { expiryDate: value })}
                    />
                  </div>
                </div>
              )}

              {isActDone(selectedAct) ? (
                <div className="w-full mt-4 space-y-2">
                  <div className="rounded-xl border border-green-200 bg-green-50/80 px-4 py-3 flex items-center gap-3 text-green-800">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-green-600" />
                    <div>
                      <p className="text-sm font-semibold">Acte {selectedAct.sequence} validé</p>
                      <p className="text-xs text-green-700/90 mt-0.5">
                        Pour modifier les photos ou la traçabilité, annulez d&apos;abord la validation.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="w-full btn-secondary py-2.5 text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    onClick={() => setPendingUnconfirm(true)}
                    disabled={unconfirming || !!photoBusy || !!deletingPhotoId}
                  >
                    {unconfirming ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Annuler la validation pour modifier
                  </button>
                </div>
              ) : (
                <>
                  {selectedRequirementMessage && (
                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 leading-snug">
                      <span className="font-semibold">Obligatoire — </span>
                      {selectedRequirementMessage}
                    </div>
                  )}
                  <button
                    type="button"
                    className="w-full mt-3 btn-primary py-3 text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    onClick={() => void markActDone()}
                    disabled={!canMarkSelectedActDone || markingActDone || !!photoBusy || !!deletingPhotoId}
                    title={selectedRequirementMessage || undefined}
                  >
                    {markingActDone ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    ✓ Acte {selectedAct.sequence} terminé
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="hidden lg:block mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="hidden lg:block fixed bottom-0 left-0 w-full bg-white/80 backdrop-blur border-t border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex gap-3">
          <button
            className="btn-secondary h-14 px-8 flex items-center gap-2 flex-shrink-0"
            onClick={() => navigate(`/practitioner/piercing/${appointmentId}/contract`)}
            disabled={saving}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
            Précédent
          </button>
          <button
            className="btn-primary h-14 flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
            onClick={() => void handleContinue()}
            disabled={saving}
          >
            <span className="font-bold text-sm uppercase tracking-widest">
              {saving ? 'Enregistrement...' : 'Confirmer et passer en caisse →'}
            </span>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="hidden lg:block pb-28" />

      <ConfirmModal
        isOpen={pendingDelete !== null}
        title="Supprimer cette image ?"
        message={
          pendingDelete?.slot === 'label'
            ? "Vous êtes sur le point de supprimer une photo de traçabilité (étiquettes). Cette action est irréversible."
            : "Vous êtes sur le point de supprimer une photo de résultat. Cette action est irréversible."
        }
        confirmLabel="Supprimer l'image"
        cancelLabel="Annuler"
        confirmVariant="danger"
        isLoading={!!deletingPhotoId}
        onConfirm={() => {
          if (!pendingDelete) return;
          void removePhoto(pendingDelete.actId, pendingDelete.slot, pendingDelete.photoId);
        }}
        onClose={() => {
          if (deletingPhotoId) return;
          setPendingDelete(null);
        }}
      />

      <ConfirmModal
        isOpen={pendingUnconfirm}
        title="Annuler la validation de l'acte ?"
        message="L'acte ne sera plus considéré comme terminé. Vous pourrez à nouveau modifier, ajouter ou supprimer les photos et la traçabilité, puis le revalider."
        confirmLabel="Annuler la validation"
        cancelLabel="Conserver la validation"
        confirmVariant="warning"
        isLoading={unconfirming}
        onConfirm={() => void unconfirmAct()}
        onClose={() => {
          if (unconfirming) return;
          setPendingUnconfirm(false);
        }}
      />

      <ConfirmDefinitiveDeleteModal
        isOpen={actPendingDelete !== null}
        count={1}
        entityLabel={
          actPendingDelete
            ? `acte #${actPendingDelete.sequence}, y compris toutes les photos, le contrat et les documents associés`
            : 'acte'
        }
        isLoading={!!deletingActId}
        onClose={() => {
          if (deletingActId) return;
          setActPendingDelete(null);
        }}
        onConfirm={() => {
          if (!actPendingDelete) return;
          void executeDeleteAct(actPendingDelete);
        }}
      />
    </>
  );
};

export default PiercingCapturePage;
